import axios from 'axios';
import 'url-search-params-polyfill';
import { SearchFilters } from '../utils/search';

import { clubs } from '../../constants';

export const createClub = (axiosInstance, values) => {
    const url = `v1/club`;
    const postParams = {
        name: values.clubName,
        description: values.about,
        url: values.website,
        location: values.location,
        private: values.private,
        clubType: values.clubType,
        isPro: values.isPro,
        isTournamentClub: values.isTournamentClub,
        subscriptionInfo: values.subscriptionInfo,
    };
    return axiosInstance.post(url, postParams);
};

export const createClubFromHighSchool = (axiosInstance, schoolId, clubSubTypeId) => {
    const url = `v1/club/hs`;
    const postParams = {
        schoolId,
        clubSubTypeId,
    };
    return axiosInstance.post(url, postParams);
};

export const updateClub = (axiosInstance, values) => {
    const url = `v1/club/${values.id}`;
    const postParams = {
        name: values.clubName,
        description: values.about,
        url: values.website,
        private: values.private,
    };
    return axiosInstance.put(url, postParams);
};

export const deleteClub = (axiosInstance, clubId) => {
    const url = `v1/club/${clubId}`;
    return axiosInstance.delete(url);
};

export const getAllClubs = (axiosInstance) => {
    const url = `v1/club`;
    return axiosInstance.get(url);
};

export const getClub = (axiosInstance, clubId) => {
    const url = `v1/club/${clubId}`;
    const params = {
        optimized: true,
    };
    return axiosInstance.get(url, {
        params,
    });
};

export const getClubEventsList = (axiosInstance, clubId, start, count, hsEventType, includeUnpublishedEvents) => {
    const url = `v1/club/${clubId}/events/list`;
    const params = {
        start: start || 0,
        count: count || 10,
        hsEventType,
        includeUnpublishedEvents: includeUnpublishedEvents || false,
    };
    return axiosInstance.get(url, {
        params,
    });
};

export const getClubDraftEventsList = (axiosInstance, clubId, start, count, hsEventType) => {
    const url = `v1/club/${clubId}/events/draft/list`;
    const params = {
        start: start || 0,
        count: count || 10,
        hsEventType,
    };
    return axiosInstance.get(url, {
        params,
    });
};

export const getClubSchoolDetails = (axiosInstance, clubId, params = {}) => {
    const url = `v1/club/${clubId}/school`;
    return axiosInstance.get(url, {
        params,
    });
};

export const getClubLeagues = (axiosInstance, clubId) => {
    const url = `v1/clubs/${clubId}/leagues`;
    return axiosInstance.get(url);
};

export const getClubMembers = (axiosInstance, clubId) => {
    const url = `/v1/club/${clubId}/clubmembers`;
    return axiosInstance.get(url);
};

export const getClubOfficialMembers = (axiosInstance, clubId) => {
    const url = `/v1/club/${clubId}/members/officials`;
    return axiosInstance.get(url);
};

export const getClubEvents = (axiosInstance, clubId) => {
    const url = `/v1/club/${clubId}/events`;
    return axiosInstance.get(url);
};
export const getClubPaymentAccounts = (axiosInstance, clubId) => {
    const url = `/v1/club/${clubId}/payment-accounts`;
    return axiosInstance.get(url);
};

export const addAdminsToClub = (axiosInstance, clubId, membersList) => {
    const url = `v1/club/${clubId}/memberadmins`;
    return axiosInstance.post(url, membersList);
};

export const inviteMembersToClub = (axiosInstance, clubId, membersList) => {
    const url = `v1/club/${clubId}/members`;
    return axiosInstance.post(url, membersList);
};

export const getClubInvites = (axiosInstance, clubId) => {
    const url = `v1/club/${clubId}/invites`;
    return axiosInstance.get(url);
};

export const addMemberToClub = (axiosInstance, clubId, values) => {
    const url = `v1/club/${clubId}/enterprise/member?id=${clubId}`;
    return axiosInstance.post(url, values);
};

export const updateMemberRole = (axiosInstance, clubId, memberId, memberRole) => {
    if (memberRole === 'FormerMember') {
        const url = `v1/club/${clubId}/members/${memberId}`;
        return axiosInstance.delete(url);
    }
    const url = `v1/club/${clubId}/members/updaterole`;
    return axiosInstance.post(url, {
        memberId,
        role: memberRole,
    });
};

export const updateMemberType = (axiosInstance, clubId, memberId, isPhysicalMember) => {
    const url = `v1/club/${clubId}/enterprise/member/updatetype`;
    return axiosInstance.post(url, {
        memberId,
        isPhysicalMember,
    });
};

export const updateClubMemberPower = (axiosInstance, clubId, memberId, isPower) => {
    const url = `v1/club/${clubId}/enterprise/member/updatepower`;
    return axiosInstance.post(url, {
        memberId,
        isPower,
    });
};

// TBD figure out what to do with this stuff
export const setupStripeAccount = (axiosInstance, clubId, data) => {
    const url = `/v1/club/${clubId}/stripe`;
    return axiosInstance.post(url, data);
};

export const requestToJoin = (axiosInstance, clubId) => {
    const url = `v1/club/${clubId}/request`;
    return axiosInstance.post(url);
};

export const requestToUnfollow = (axiosInstance, clubId) => {
    const url = `v1/club/${clubId}/unfollow`;
    return axiosInstance.post(url);
};

export const approveOrRejectRequest = (axiosInstance, clubId, memberId, requestId, value) => {
    const url = `v1/club/${clubId}/request/${memberId}/${value}`;
    return axiosInstance.post(url);
};

export const resendInvite = (axiosInstance, clubId, inviteId) => {
    const url = `v1/club/${clubId}/invite/${inviteId}/resend`;
    return axiosInstance.post(url);
};

export const deleteInvite = (axiosInstance, clubId, inviteId) => {
    const url = `v1/club/${clubId}/invite/${inviteId}/delete`;
    return axiosInstance.post(url);
};

export const applyForVerifiedClub = (axiosInstance, clubId) => {
    return axiosInstance.post(`/v1/club/${clubId}/verified`);
};

export const applyForAutoVerifyClub = (axiosInstance, clubId) => {
    return axiosInstance.post(`/v1/club/${clubId}/autoverified`);
};

export const addTeamRolesToClub = (axiosInstance, clubId, teamRolesList) => {
    return axiosInstance.post(`v1/club/${clubId}/teams/roles`, teamRolesList);
};

export const removeTeamRoleFromClub = (axiosInstance, clubId, teamRoleId) => {
    return axiosInstance.delete(`v1/club/${clubId}/teams/role/${teamRoleId}`);
};

export const addTeamTennisRole = (axiosInstance, clubId, teamRolesList) => {
    return axiosInstance.post(`/v1/club/${clubId}/captains/roles`, teamRolesList);
};

export const removeTeamTennisRole = (axiosInstance, clubId, teamRoleId) => {
    return axiosInstance.delete(`v1/club/${clubId}/captains/role/${teamRoleId}`);
};

export const addPlayerToRoster = (axiosInstance, clubId, playerData) => {
    return axiosInstance.post(`v1/club/${clubId}/school/roster/add`, playerData);
};

export const updatePlayer = (axiosInstance, clubId, playerData) => {
    return axiosInstance.put(`v1/club/${clubId}/school/roster/update`, playerData);
};

export const removePlayerFromRoster = (axiosInstance, clubId, playerId) => {
    return axiosInstance.delete(`v1/club/${clubId}/school/roster/${playerId}`);
};

export const fetchClubResults = (axiosInstance, reqParams, clubId) => {
    const params = {};
    const url = `v1/club/${clubId}/clubresults`;

    params.type = reqParams.resultsType || 's';
    params.start = reqParams.start || 0;
    params.count = reqParams.count || clubs.resultsDefaultPageSize;
    params.optimized = true;

    return axiosInstance.get(url, {
        params,
    });
};

export const clubMeta = (axiosInstance, clubId) => {
    const url = `v1/club/${clubId}/meta`;

    return axiosInstance.get(url);
};

export const sendMessage = (axiosInstance, message, clubId) => {
    const url = `v1/club/${clubId}/sendemailnotification`;
    return axiosInstance.post(url, {
        message,
    });
};

export const sendEventInviteToMembers = (axiosInstance, eventId, clubId) => {
    const url = `v1/club/${clubId}/send-event-invite`;
    return axiosInstance.post(url, {
        eventId,
    });
};

export const power6Stats = (axiosInstance, conferenceId, gender) => {
    const url = `v1/college/conference/${conferenceId}/p6strength`;
    return axiosInstance.get(url, {
        params: {
            gender,
        },
    });
};

let searchCancelToken;

const DEFAULT_CLUB_MEMBER_SORT = { field: 'firstName', order: 'asc' };
const DEFAULT_PAGE_SIZE = 40;
const UTR_MIN_BOUND = 1;
const UTR_MAX_BOUND = 16;
const CLUB_MEMBER_TYPE_DIGITAL = 2;

export const memberTypeForClubMemberTypeId = (clubMemberTypeId) => {
    if (!clubMemberTypeId) {
        return null;
    }
    return clubMemberTypeId === CLUB_MEMBER_TYPE_DIGITAL ? 'Digital' : 'Physical';
};

// Each entry is applied in order; first truthy `value(filters)` is appended.
const ORDERED_FILTER_APPENDERS = [
    { param: 'query', value: f => f.query },
    {
        apply: (params, f) => {
            if (f.distance && f.lat && f.lng) {
                params.append('distance', f.distance);
                params.append('pin', `${f.lat},${f.lng}`);
            }
        },
    },
    { param: 'gender', value: f => f.gender },
    { param: 'utrMin', value: f => (f.utrMin > UTR_MIN_BOUND ? f.utrMin : null) },
    { param: 'utrMax', value: f => (f.utrMax < UTR_MAX_BOUND ? f.utrMax : null) },
    { param: 'utrType', value: f => f.utrType },
    { param: 'utrTeamType', value: f => f.utrTeamType },
    { param: 'utrReliability', value: f => f.utrReliability },
    { param: 'memberType', value: f => memberTypeForClubMemberTypeId(f.clubMemberTypeId) },
    {
        apply: (params, f) => {
            const sportTypes = f.sportTypes || {};
            params.append(SearchFilters.SHOW_TENNIS_CONTENT, !!sportTypes.tennis);
            params.append(SearchFilters.SHOW_PICKLEBALL_CONTENT, !!sportTypes.pickleball);
        },
    },
    { param: 'locationCountryName', value: f => f.locationCountryName },
    { param: 'locationStateName', value: f => f.locationStateName },
];

export const appendSearchClubMembersFilters = (params, filters) => {
    if (!filters) {
        return;
    }
    ORDERED_FILTER_APPENDERS.forEach(({ param, value, apply }) => {
        if (apply) {
            apply(params, filters);
            return;
        }
        const v = value(filters);
        if (v) {
            params.append(param, v);
        }
    });
};

export const appendSearchClubMembersSort = (params, sort) => {
    if (!sort) {
        return;
    }
    const sortList = Array.isArray(sort) ? sort : [sort];
    sortList.forEach(({ field, order }) => {
        params.append('sort', `${field}:${order}`);
    });
};

export const appendSearchClubMembersExcludes = (params, excludeMemberIds) => {
    if (!Array.isArray(excludeMemberIds)) {
        return;
    }
    excludeMemberIds.forEach(id => {
        params.append('filter', `memberId:!${id}`);
    });
};

export const buildSearchClubMembersParams = (clubId, searchParams) => {
    const params = new URLSearchParams();
    const {
        filters,
        top,
        skip,
        sort = DEFAULT_CLUB_MEMBER_SORT,
        excludeMemberIds = [],
    } = searchParams || {};
    if (clubId) {
        params.append('clubId', clubId);
    }
    params.append('top', top || DEFAULT_PAGE_SIZE);
    params.append('skip', skip || 0);
    appendSearchClubMembersFilters(params, filters);
    appendSearchClubMembersSort(params, sort);
    appendSearchClubMembersExcludes(params, excludeMemberIds);
    return params;
};

export const searchClubMembers = (axiosInstance, clubId, searchParams) => {
    if (searchCancelToken) {
        searchCancelToken();
    }
    const { CancelToken } = axios;
    return axiosInstance.get(`v2/search/club/members`, {
        params: buildSearchClubMembersParams(clubId, searchParams),
        cancelToken: new CancelToken((c) => {
            searchCancelToken = c;
        }),
    });
};

export const removeClubMembers = (axiosInstance, clubId, payload) => {
    const url = `v1/club/${clubId}/members/remove`;
    return axiosInstance.post(url, payload);
};

export const changeMembershipType = (axiosInstance, clubId, payload) => {
    const url = `v1/club/${clubId}/members/type/update`;
    return axiosInstance.post(url, payload);
};

export const getClubOrganizers = (axiosInstance, clubId) => {
    return axiosInstance.get(`v1/Club/${clubId}/clubmembers`, {
        params: { roles: 'Owner,Admin', count: 50 },
    });
};

export const highSchoolStandingsSeasons = (axiosInstance, clubId) => {
    const url = `v1/highschool/${clubId}/seasons`;
    return axiosInstance.get(url);
};

export const highSchoolStandings = (axiosInstance, clubId, seasonYear) => {
    const params = {
        seasonYear,
    };
    const url = `v1/highschool/${clubId}/standings`;
    return axiosInstance.get(url, {
        params,
    });
};

export const getAdultTeamTennisRoster = (axiosInstance, clubId) => {
    const url = `/v1/club/${clubId}/roster`;
    return axiosInstance.get(url);
};

export const getAdultTeamTennisEvents = (axiosInstance, clubId) => {
    const url = `/v1/club/${clubId}/teammatches`;
    return axiosInstance.get(url);
};

export const getAdultTeamTennisStandings = (axiosInstance, clubId) => {
    const url = `v1/teams/${clubId}/standings`;
    return axiosInstance.get(url);
};

export const includeInAttStandings = (axiosInstance, eventId) => {
    const url = `/v1/tms/events/${eventId}/includeconference`;
    return axiosInstance.post(url);
};

export const omitFromAttStandings = (axiosInstance, eventId) => {
    const url = `/v1/tms/events/${eventId}/omitconference`;
    return axiosInstance.post(url);
};

export const getClubTeamRatingsByClubId = (axiosInstance, clubId, snapshotDateLabelParams) => {
    const url = `v1/club/team-rating/${clubId}`;

    if (snapshotDateLabelParams) {
        const params = {
                snapshotDateLabel: snapshotDateLabelParams,
            };
        return axiosInstance.get(url, {
            params,
        });
    }
    return axiosInstance.get(url);
};
